/**
 * Serac product marketing page (serac.build).
 *
 * Written for a solution-aware reader: a ServiceNow developer who has already
 * decided to use an agent and is comparing approaches. Section order, top to
 * bottom:
 *
 *   TopNav · Hero · Guards · DeferredTools · Pitfalls · Story ·
 *   Alternatives · Licence · Proof · InstallFaq · Pricing · FinalCTA · Footer
 *
 * Everything between Hero and FinalCTA lives in components/marketing/Section*.tsx
 * and takes no props; this file owns the Hero, the Story, the final CTA and the
 * page meta. Shared shell pieces (TopNav, Footer, PricingSection) live in
 * components/marketing/ so the Pricing page can reuse them.
 *
 * Two conventions this file holds to:
 *  - Every user-visible string comes from the `marketing` namespace. The one
 *    exception is MCP_SERVERS_CONFIG below, which is a code artefact rather
 *    than copy: it is an MCP client config, so it must read identically in en
 *    and nl and translating it would falsify it.
 *  - The ice accent is reserved for things the visitor can act on, and for one
 *    of them per block: in the hero it is the hosted CTA, so the install chip
 *    beside it is bordered in --border-strong. Headlines, mocks and rules use
 *    --fg / --border — including the approval buttons drawn in the hero frame,
 *    which are a picture of a control rather than a control.
 */
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { Button, ChromeBar, Icon, Tag } from '../components/serac';
import SeracTopNav from '../components/marketing/SeracTopNav';
import SeracMarketingFooter from '../components/marketing/SeracMarketingFooter';
import SeracPricingSection from '../components/marketing/SeracPricingSection';
import SectionGuards from '../components/marketing/SectionGuards';
import SectionDeferredTools from '../components/marketing/SectionDeferredTools';
import SectionPitfalls from '../components/marketing/SectionPitfalls';
import SectionAlternatives from '../components/marketing/SectionAlternatives';
import SectionLicence from '../components/marketing/SectionLicence';
import SectionProof from '../components/marketing/SectionProof';
import SectionInstallFaq from '../components/marketing/SectionInstallFaq';
import { useIsDesktop, useIsMobile } from '../hooks/useMediaQuery';
import { usePageMeta } from '../hooks/usePageMeta';

/**
 * The client config the install command needs next. Shape and variable names
 * from packages/servicenow-mcp/README.md on serac-labs/serac main ("Point any
 * MCP client at the `servicenow-mcp-stdio` binary"); SNOW_INSTANCE is the name
 * the README says to prefer — SERVICENOW_INSTANCE_URL and SNOW_INSTANCE_URL are
 * the accepted aliases, the second only from 0.2.2 onwards.
 *
 * SNOW_ENVIRONMENT_TYPE is the line the README's auth example leaves out, and
 * it is load-bearing for what this hero promises. handlers/call-tool.ts gates
 * the production refusal on `context.environmentType === "production"`, and
 * shared/context-loader.ts populates that field from exactly two variables
 * (ENV_VARS.environmentType = SERVICENOW_ENVIRONMENT_TYPE, SNOW_ENVIRONMENT_TYPE)
 * — absent or unrecognised leaves it undefined and the guard never fires. Ship
 * the three-variable auth snippet on its own and the visitor gets a server that
 * cannot refuse a production write, while the subtitle, the trust row, the frame
 * beside it and the JSON-LD all say it will. The instance host matches the one
 * in the terminal frame so the config and the transcript are one session.
 *
 * The update-set guard in the same file carries no such condition — it is
 * unconditional — so that half holds whatever this block says.
 */
const MCP_SERVERS_CONFIG = `{
  "mcpServers": {
    "servicenow": {
      "command": "servicenow-mcp-stdio",
      "env": {
        "SNOW_INSTANCE": "https://acme.service-now.com",
        "SNOW_CLIENT_ID": "…",
        "SNOW_CLIENT_SECRET": "…",
        "SNOW_ENVIRONMENT_TYPE": "production"
      }
    }
  }
}`;

const REPO_URL = 'https://github.com/serac-labs/serac';

// Height of the sticky SeracTopNav, so a hash jump does not park the section
// heading underneath it.
const HEADER_OFFSET = 80;

// Wraps small buttons so the <a> meets the 44x44 mobile tap-target rule
// without changing the button visuals — the link gains invisible padding
// around the rendered button.
const tapTargetWrap = {
  textDecoration: 'none',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: 44,
  minWidth: 44,
} as const;

// Skip rendering / style / layout work for a section until it's near
// the viewport. Massive mobile win — most CSS recalc time is from
// sections nobody scrolls to. The intrinsic-size hint keeps the page
// scroll height stable while the section is in skipped state.
function Defer({ children, height, id }: { children: React.ReactNode; height: number; id?: string }) {
  return (
    <div id={id} style={{ contentVisibility: 'auto', containIntrinsicSize: `auto ${height}px` }}>
      {children}
    </div>
  );
}

function copyToClipboard(text: string, done: string) {
  void navigator.clipboard
    ?.writeText(text)
    .then(() => toast.success(done))
    .catch(() => {});
}

/**
 * The H1 wants a line break; the <title>, the social descriptions and the
 * JSON-LD want one sentence. Deriving the break from the sentence keeps that to
 * one key: a second and third key holding the halves is three strings carrying
 * one claim with nothing enforcing that they agree, which is how the old page
 * ended up with a headline that did not match its own <title>.
 *
 * Both locales break at the em dash, and the dash belongs to the first line.
 * A headline without one renders unbroken rather than guessing.
 */
function headlineLines(headline: string): [string] | [string, string] {
  const at = headline.indexOf(' — ');
  if (at < 0) return [headline];
  return [`${headline.slice(0, at)} —`, headline.slice(at + 3)];
}

export default function MarketingHome() {
  const navigate = useNavigate();
  const { t } = useTranslation('marketing');

  // The H1, the <title>, both social descriptions and the JSON-LD read these
  // two keys and nothing else — the H1's line break is derived, not stored, so
  // there is no second copy of the sentence to drift out of agreement.
  // index.html still holds a hand-maintained copy for the pre-hydration crawl.
  const headline = t('hero.headline');
  const subtitle = t('hero.subtitle');

  usePageMeta({
    title: headline,
    description: subtitle,
    canonical: 'https://serac.build/',
    ogType: 'website',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Serac',
        alternateName: ['Serac ServiceNow MCP server', 'Snow-Flow', 'Snow Flow'],
        applicationCategory: 'DeveloperApplication',
        applicationSubCategory: 'ServiceNow MCP server for AI agents',
        operatingSystem: 'macOS, Linux, Windows, Web',
        url: 'https://serac.build',
        sameAs: [
          REPO_URL,
          'https://www.npmjs.com/package/@serac-labs/servicenow-mcp',
          'https://docs.serac.build',
          'https://discord.gg/uGGrWj9KsT',
        ],
        description: `${headline} ${subtitle}`,
        // No `offers`. This entity is Serac, and Serac is not one price: the
        // server is free, the workspace tiers are not, and the tier prices on
        // this page come from the generated modules.json at runtime — which
        // this component does not read. A single €0 offer here would be a
        // structured-data claim the pricing section contradicts, and hardcoding
        // the paid ones would be the duplicate that manifest exists to prevent.
        publisher: {
          '@type': 'Organization',
          name: 'Seraclabs B.V.',
          url: 'https://serac.build',
          logo: { '@type': 'ImageObject', url: 'https://serac.build/icon-512.png' },
          address: {
            '@type': 'PostalAddress',
            streetAddress: 'Fabriekstraat 20',
            postalCode: '7311GP',
            addressLocality: 'Apeldoorn',
            addressCountry: 'NL',
          },
        },
        featureList: [
          'A ServiceNow MCP server for Claude Code, opencode, or any MCP client',
          'Refuses a configuration write while no update set is active for the session',
          // "classified as" is load-bearing, not hedging: the guard reads
          // context.environmentType, which only exists once the instance has
          // been marked production (SNOW_ENVIRONMENT_TYPE self-hosted, a field
          // on the instance in the workspace). Unqualified, this line would
          // promise a refusal on an instance nobody has told the server about.
          'Refuses a write against an instance classified as production until a person approves that exact call',
          'Deferred tools: tools/list answers with two meta-tools, tool_search and tool_execute',
          'Practitioner-written ServiceNow skill guides in Markdown — ES5/Rhino, update-set workflow, Flow Designer, blast radius',
          'Bring your own LLM keys — any provider, including OpenAI-compatible endpoints',
          'Apache License 2.0 — no carve-outs, no resale restriction',
          'Run it inside your own agent, hosted, or on your own hardware',
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        name: 'Seraclabs B.V.',
        url: 'https://serac.build',
        logo: 'https://serac.build/icon-512.png',
        sameAs: ['https://github.com/serac-labs', 'https://discord.gg/uGGrWj9KsT'],
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Fabriekstraat 20',
          postalCode: '7311GP',
          addressLocality: 'Apeldoorn',
          addressCountry: 'NL',
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        name: 'Serac',
        url: 'https://serac.build',
        publisher: { '@type': 'Organization', name: 'Seraclabs B.V.' },
      },
    ],
  });

  const onOpenApp = () => {
    if (typeof window !== 'undefined' && /^(www\.)?serac\.build$/.test(window.location.hostname)) {
      window.location.href = 'https://dashboard.serac.build/login';
      return;
    }
    navigate('/login');
  };

  // The hero's primary action promises a workspace, so it must not land on a
  // password field for an account the visitor does not have. /login defaults to
  // the sign-in tab (Home.tsx useState<LoginTab>('sign-in')); /register is the
  // same page with create-account selected (Home.tsx:122). The nav and the
  // final CTA keep onOpenApp — they say "open dashboard", which is the
  // returning user's door.
  const onStartFree = () => {
    if (typeof window !== 'undefined' && /^(www\.)?serac\.build$/.test(window.location.hostname)) {
      window.location.href = 'https://dashboard.serac.build/register';
      return;
    }
    navigate('/register');
  };

  // The nav links here with react-router (`<Link to="/#product">`), which sets
  // location.hash without the browser's native anchor jump, so nothing used to
  // move at all. The Lenis-era scroll-padding this replaces was styling an
  // anchor navigation that never happened.
  //
  // One scroll is not enough either. Every section below the hero sits inside a
  // <Defer> wrapper with content-visibility:auto, so until a section is near the
  // viewport its height is the containIntrinsicSize guess rather than its real
  // one. Measure a target four sections down and you are adding up four guesses;
  // scroll there and the sections you just brought into view render at their
  // true heights and move the target out from under you.
  //
  // So: jump, re-measure on the next frame, jump again, until the number stops
  // changing. It converges in two or three frames because each pass paints the
  // sections it scrolled past. Instant rather than smooth — this runs on a page
  // load or a cross-page nav, where an animation would only delay the answer,
  // and reduced-motion users get the same behaviour as everyone else.
  const { hash } = useLocation();
  useEffect(() => {
    const id = hash.replace(/^#/, '');
    if (!id) return;
    let frame = 0;
    let passes = 0;
    const settle = () => {
      const target = document.getElementById(id);
      if (!target) return;
      const top = Math.max(0, target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET);
      const settled = Math.abs(top - window.scrollY) < 2;
      if (!settled) window.scrollTo({ top, behavior: 'auto' });
      passes += 1;
      if (!settled && passes < 12) frame = requestAnimationFrame(settle);
    };
    frame = requestAnimationFrame(settle);
    return () => cancelAnimationFrame(frame);
  }, [hash]);

  return (
    <div
      style={{
        background: 'var(--bg)',
        color: 'var(--fg)',
        minHeight: '100vh',
        position: 'relative',
        fontFamily: 'var(--font-text)',
      }}
    >
      <ChromeBar active="build" />
      <SeracTopNav active="product" />
      <main>
        <Hero onStartFree={onStartFree} />
        <Defer height={900}><SectionGuards /></Defer>
        <Defer height={520}><SectionDeferredTools /></Defer>
        <Defer height={900}><SectionPitfalls /></Defer>
        <Defer height={1200}><StorySection /></Defer>
        <Defer height={800}><SectionAlternatives /></Defer>
        {/* The nav's /#integrations link used to point at the LLM tile grid.
            That grid is gone; the ownership + licence section is where "whose
            model, whose keys, whose instance" is answered now. */}
        <Defer height={900} id="integrations"><SectionLicence /></Defer>
        <Defer height={520}><SectionProof /></Defer>
        <Defer height={700}><SectionInstallFaq /></Defer>
        <Defer height={1600}><SeracPricingSection /></Defer>
        <Defer height={420}><FinalCTA onOpenApp={onOpenApp} /></Defer>
      </main>
      <Defer height={600}><SeracMarketingFooter /></Defer>
    </div>
  );
}

/* ---------------- HERO ---------------- */

function Hero({ onStartFree }: { onStartFree: () => void }) {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();
  // Two columns only from 1024px. iPad portrait (768–1023) used to get the
  // desktop split, which left the terminal about 350px wide — and the mono
  // lines in it break mid-token at that width because they wrap on `anywhere`.
  const isStacked = !useIsDesktop();
  const [showConfig, setShowConfig] = useState(false);
  const lines = headlineLines(t('hero.headline'));

  return (
    <section
      style={{
        padding: isMobile ? '48px 16px 56px' : '72px 32px 88px',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          maxWidth: 1200,
          margin: '0 auto',
          display: 'grid',
          gridTemplateColumns: isStacked ? '1fr' : 'minmax(0, 1.02fr) minmax(0, 0.98fr)',
          gap: isStacked ? 36 : 56,
          alignItems: 'center',
        }}
      >
        <div>
          <h1
            className="t-display"
            style={{
              fontSize: 'clamp(29px, 3.5vw, 46px)',
              margin: 0,
              color: 'var(--fg)',
              lineHeight: 1.08,
            }}
          >
            {lines[0]}
            {lines.length === 2 && (
              <>
                <br />
                {lines[1]}
              </>
            )}
          </h1>

          <p
            style={{
              fontSize: isMobile ? 15 : 18,
              lineHeight: 1.55,
              color: 'var(--fg-muted)',
              maxWidth: 560,
              margin: isMobile ? '18px 0 26px' : '24px 0 32px',
            }}
          >
            {t('hero.subtitle')}
          </p>

          {/* Two doors, and each one says what it costs before you take it.
              They are stacked rather than set side by side because side by side
              does not fit: the install command is ~350px of 13px mono and the
              hosted button another ~215px, in a column that is 570px at its
              widest — and shrinking the command to buy that row is the one
              trade this hero could not make. Rank is carried by the accent
              instead. The ice fill moved from the install chip to the hosted
              button; the chip keeps its place first, its 44px height, its
              monospace and its click-to-copy. */}
          <div style={{ display: 'grid', gap: 8, justifyItems: 'start' }}>
            <button
              type="button"
              aria-label={t('hero.ctaCliCopy')}
              onClick={() => copyToClipboard(t('hero.ctaCli'), t('hero.ctaCliCopied'))}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 12,
                minHeight: 44,
                padding: '0 14px',
                borderRadius: 8,
                background: 'var(--ink-050)',
                border: '1px solid var(--border-strong)',
                color: 'var(--fg)',
                cursor: 'pointer',
                fontFamily: 'var(--font-mono)',
                fontSize: isMobile ? 12 : 13,
              }}
            >
              <span style={{ color: 'var(--fg-dim)' }}>$</span>
              <span style={{ overflowWrap: 'anywhere', textAlign: 'left' }}>{t('hero.ctaCli')}</span>
              <span style={{ color: 'var(--fg-dim)', display: 'inline-flex' }}>
                <Icon.copy s={14} />
              </span>
            </button>

            <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--fg-dim)', maxWidth: 460 }}>
              {t('hero.ctaCliSub')}
            </p>
          </div>

          {/* The command alone does not get you connected; the client config is
              the next thing you need, one click away rather than a docs trip. */}
          <div style={{ marginTop: 8 }}>
            <button
              type="button"
              onClick={() => setShowConfig((v) => !v)}
              aria-expanded={showConfig}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
                minHeight: 32,
                padding: 0,
                background: 'none',
                border: 'none',
                color: 'var(--fg-muted)',
                fontFamily: 'var(--font-text)',
                fontSize: 13,
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
              }}
            >
              {t('hero.ctaConfig')}
            </button>

            {showConfig && (
              <div
                style={{
                  marginTop: 10,
                  position: 'relative',
                  background: 'var(--ink-000)',
                  border: '1px solid var(--border)',
                  borderRadius: 10,
                  padding: '12px 14px',
                  maxWidth: 560,
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    lineHeight: 1.6,
                    color: 'var(--fg-muted)',
                    overflowX: 'auto',
                  }}
                >
                  {MCP_SERVERS_CONFIG}
                </pre>
                <button
                  type="button"
                  aria-label={t('hero.ctaConfigCopy')}
                  onClick={() => copyToClipboard(MCP_SERVERS_CONFIG, t('hero.ctaConfigCopied'))}
                  style={{
                    marginTop: 10,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 8,
                    minHeight: 32,
                    padding: '0 10px',
                    borderRadius: 6,
                    background: 'transparent',
                    border: '1px solid var(--border-strong)',
                    color: 'var(--fg-muted)',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  <Icon.copy s={12} />
                  {t('hero.ctaConfigCopy')}
                </button>
              </div>
            )}
          </div>

          {/* The second door. Its microcopy is the reason it is safe to put a
              signup this high for this reader: every clause is a fact from
              TIER_PRESETS.free (no card, one instance, seven days) plus the
              thing those limits are NOT — the free workspace runs the same
              write-capable agent, because FREE_TIER_MODULE_IDS carries
              'pro-tier'. "Bounded by scale, never by what the agent can do" is
              that constant's own comment. No price: the tier numbers on this
              page come from the generated modules.json at runtime, and a second
              copy in the hero is exactly the drift that manifest prevents. */}
          <div style={{ marginTop: isMobile ? 22 : 26, display: 'grid', gap: 8, justifyItems: 'start' }}>
            <Button
              variant="ice"
              size={isMobile ? 'md' : 'lg'}
              onClick={onStartFree}
              iconRight={<Icon.arrow s={14} />}
            >
              {t('hero.ctaHosted')}
            </Button>

            <p style={{ margin: 0, fontSize: 12, lineHeight: 1.5, color: 'var(--fg-dim)', maxWidth: 460 }}>
              {t('hero.ctaHostedSub')}
            </p>
          </div>

          <div
            style={{
              marginTop: isMobile ? 28 : 36,
              display: 'flex',
              alignItems: 'center',
              gap: isMobile ? 12 : 20,
              flexWrap: 'wrap',
              fontSize: isMobile ? 12 : 13,
              color: 'var(--fg-dim)',
            }}
          >
            {/* flex-start, not center: two of the three items now carry a
                second clause and wrap to two lines on a phone, and a tick
                centred against a two-line item reads as a bullet that has
                slipped. */}
            {[t('hero.trust.elastic'), t('hero.trust.anyLLM'), t('hero.trust.selfhost')].map((claim) => (
              <span key={claim} style={{ display: 'inline-flex', alignItems: 'flex-start', gap: 8 }}>
                <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 3 }}>
                  <Icon.check s={12} />
                </span>
                {claim}
              </span>
            ))}
          </div>
        </div>

        <div id="product">
          <HeroTerminal />
        </div>
      </div>
    </section>
  );
}

/**
 * Three beats of a real session in the client the visitor already runs:
 * tools/list answers with two meta-tools, one tool_search, one tool_execute —
 * and the execute ends on the production-write refusal rather than on a result.
 *
 * The refusal is drawn as what the tool call returns, indented under the call
 * like any other result, because that is what it is here: handlers/call-tool.ts
 * throws an McpError and prodWriteBlockMessage() tells the model to surface it
 * and re-issue only after the user approves. There is no Serac-rendered Approve
 * button in Claude Code — that card is the hosted workspace's
 * components/chat/ToolInvocationCard.tsx, and drawing its buttons inside a
 * `claude code` window would be the same borrowed-console mistake as the portal
 * screenshot this hero replaced.
 *
 * Which is why the hosted half is a separate surface BELOW the terminal chrome
 * rather than a card inside it: same panel, same session, same call — the
 * divider says so — but visibly not the same window. It is where the page's two
 * halves meet, so it states only the delta (the error becomes a button) and
 * leaves the refusal wording to the transcript above and the sha256 / __-flag
 * enforcement to SectionGuards below.
 *
 * Nothing here is interactive. The two labels are the literal strings
 * ToolInvocationCard renders in each locale, so the drawing matches the shipped
 * UI instead of inventing chrome — but they are <span>s, and they deliberately
 * do NOT wear the ice accent this page reserves for controls that work. A live
 * ice button and a picture of one in the same viewport is a trap.
 */
function HeroTerminal() {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        borderRadius: 12,
        overflow: 'hidden',
        border: '1px solid var(--border-strong)',
        background: 'var(--ink-000)',
        boxShadow: 'var(--shadow-3)',
      }}
    >
      <div
        style={{
          minHeight: 36,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 12px',
          flexWrap: 'wrap',
          background: 'var(--ink-100)',
          borderBottom: '1px solid var(--border)',
          fontFamily: 'var(--font-mono)',
          fontSize: 11,
          color: 'var(--fg-dim)',
        }}
      >
        <span style={{ color: 'var(--fg-muted)' }}>{t('hero.frame.url')}</span>
        <span style={{ marginLeft: 'auto' }}>{t('hero.frame.tenant')}</span>
      </div>

      <div
        style={{
          padding: isMobile ? 14 : 18,
          display: 'grid',
          gap: 12,
          fontFamily: 'var(--font-mono)',
          fontSize: isMobile ? 11 : 12,
          lineHeight: 1.65,
        }}
      >
        <TerminalStep call={t('hero.frame.listLabel')} result={t('hero.frame.listResult')} />
        <TerminalStep call={t('hero.frame.searchCall')} result={t('hero.frame.searchResult')} />
        <div>
          <TerminalStep call={t('hero.frame.execCall')} />
          {/* Indented under the call and ruled in warn, the way a returned
              result is indented in TerminalStep — no card, no buttons. */}
          <div
            style={{
              marginTop: 8,
              marginLeft: 14,
              paddingLeft: 12,
              borderLeft: '2px solid color-mix(in oklch, var(--signal-warn) 55%, var(--border))',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <span style={{ color: 'var(--signal-warn)', display: 'inline-flex' }}>
                <Icon.shield s={13} />
              </span>
              <Tag tone="warn">{t('hero.frame.blocked')}</Tag>
            </div>
            <div
              style={{
                fontFamily: 'var(--font-text)',
                fontSize: 13,
                color: 'var(--fg)',
                marginBottom: 4,
              }}
            >
              {t('hero.frame.refusalTitle')}
            </div>
            <div
              style={{
                fontFamily: 'var(--font-text)',
                fontSize: 12,
                lineHeight: 1.55,
                color: 'var(--fg-muted)',
              }}
            >
              {t('hero.frame.refusalBody')}
            </div>
          </div>
        </div>
      </div>

      <HostedStrip />

      {/* The panel's last hairline row, and the check-it/buy-it argument as an
          interface detail rather than a paragraph: mcp-http is the container
          built from the OSS package the command above installs (createMcpClient
          returns McpHttpClient by default; prod runs USE_HTTP_MCP=true). */}
      <div
        style={{
          borderTop: '1px solid var(--border)',
          padding: '8px 14px',
          fontFamily: 'var(--font-mono)',
          fontSize: 10,
          letterSpacing: '0.02em',
          color: 'var(--fg-dim)',
          overflowWrap: 'anywhere',
        }}
      >
        {t('hero.frame.origin')}
      </div>
    </div>
  );
}

/**
 * The same call, continued in the hosted workspace: one tool, the fingerprint
 * and TTL that bind an approval to it, and the two buttons.
 *
 * Every string is checkable. The labels are ToolInvocationCard.tsx's own, in
 * both locales; APPROVAL_TTL_MS in services/guard-approval.ts is 30 minutes and
 * the fingerprint there is sha256 over stably-stringified arguments; the host
 * matches the terminal's tenant line and the mcpServers snippet, so this is one
 * session throughout.
 *
 * The note says what the workspace ADDS, not what it never holds — the portal
 * does store ServiceNow credentials and LLM keys, and the ownership section
 * says so. The model line is a caption in our own voice rather than a mocked
 * ModelSelector: that component has no such string, and inventing one would be
 * the first fabricated product detail on a page that has none.
 */
function HostedStrip() {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();

  return (
    <div
      style={{
        borderTop: '1px solid var(--border)',
        background: 'var(--ink-050)',
        padding: isMobile ? 14 : 18,
        display: 'grid',
        gap: 10,
      }}
    >
      <div className="t-eyebrow" style={{ fontSize: 10, color: 'var(--fg-dim)' }}>
        {t('hero.frame.hostedDivider')}
      </div>

      {/* Two lines, not one row with the meta pushed right: the tool name and
          the fingerprint line are ~400px and ~320px of mono in a panel that is
          485px at its widest, so a right-aligned meta would wrap to its own
          line anyway and land there stranded. Indented under the call instead,
          the way TerminalStep indents a result. */}
      <div
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: isMobile ? 11 : 12,
          lineHeight: 1.65,
        }}
      >
        <div style={{ color: 'var(--fg)', overflowWrap: 'anywhere' }}>
          {t('hero.frame.hostedTool')}
        </div>
        <div style={{ paddingLeft: 14, color: 'var(--fg-dim)', overflowWrap: 'anywhere' }}>
          {t('hero.frame.hostedMeta')}
        </div>
      </div>

      {/* Spans, not buttons: this is a drawing of the shipped card, and a fake
          control that swallows a click is worse than no control at all. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 10px',
            borderRadius: 6,
            background: 'var(--ink-900)',
            color: 'var(--ink-000)',
            fontSize: 11,
            fontWeight: 600,
          }}
        >
          {t('hero.frame.hostedApprove')}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 10px',
            borderRadius: 6,
            border: '1px solid var(--border-strong)',
            color: 'var(--fg-muted)',
            fontSize: 11,
          }}
        >
          {t('hero.frame.hostedDeny')}
        </span>
      </div>

      {/* BYOK as running state on the surface you pay for, which is the whole
          point of putting it here rather than in the licence claim. */}
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-dim)' }}>
        {t('hero.frame.hostedModel')}
      </div>

      <p
        style={{
          margin: 0,
          fontFamily: 'var(--font-text)',
          fontSize: 12,
          lineHeight: 1.55,
          color: 'var(--fg-muted)',
        }}
      >
        {t('hero.frame.hostedNote')}
      </p>
    </div>
  );
}

function TerminalStep({ call, result }: { call: string; result?: string }) {
  return (
    <div>
      <div style={{ color: 'var(--fg)', overflowWrap: 'anywhere' }}>
        <span style={{ color: 'var(--fg-dim)' }}>› </span>
        {call}
      </div>
      {result && (
        <div style={{ color: 'var(--fg-muted)', overflowWrap: 'anywhere', paddingLeft: 14 }}>
          {result}
        </div>
      )}
    </div>
  );
}

/* ---------------- ONE STORY, END TO END ---------------- */

function readSteps(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((s): s is string => typeof s === 'string') : [];
}

function StorySection() {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();
  // Same 1024px rule as the hero: two 350px columns of tool names on an iPad
  // is worse than one readable column.
  const isStacked = !useIsDesktop();

  const byHand = readSteps(t('workflow.byHand.steps', { returnObjects: true }) as unknown);
  const withAgent = readSteps(t('workflow.withAgent.steps', { returnObjects: true }) as unknown);

  return (
    <section
      id="story"
      style={{ padding: isMobile ? '56px 16px' : '80px 32px', borderTop: '1px solid var(--border)' }}
    >
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div className="t-eyebrow" style={{ marginBottom: 16 }}>
          {t('workflow.eyebrow')}
        </div>
        <h2
          className="t-display"
          style={{ fontSize: 'clamp(28px, 5vw, 44px)', margin: '0 0 12px', maxWidth: 760, lineHeight: 1.15 }}
        >
          {t('workflow.headline')}
        </h2>
        <p
          style={{
            fontSize: isMobile ? 15 : 17,
            color: 'var(--fg-muted)',
            maxWidth: 720,
            lineHeight: 1.55,
            marginTop: 0,
            marginBottom: isMobile ? 28 : 40,
          }}
        >
          {t('workflow.subtitle')}
        </p>

        <div style={{ maxWidth: 640 }}>
          <StoryMock />
          <p
            style={{
              margin: '14px 0 0',
              paddingLeft: 12,
              borderLeft: '2px solid var(--border-strong)',
              fontSize: 12,
              lineHeight: 1.6,
              color: 'var(--fg-dim)',
            }}
          >
            {t('workflow.story.hostedNote')}
          </p>
        </div>

        {/* `workflow.boundary` is deliberately not rendered. It read "you export
            the same XML your team has always exported. The developer promotes;
            the agent does not" — both halves stated as things the agent cannot
            do, and the catalogue this page links to disproves both: tools.json
            on main carries snow_export_to_xml, snow_update_set_manage with an
            `export` action, snow_cicd_deploy, snow_github_deploy and
            snow_rollback_deployment. A senior developer greps that file. The
            boundary is a choice about this flow, not a missing capability, and
            the guards section already closes on it — repeating it here was the
            second copy the brief forbids. Left for the copy owner to re-aim or
            retire; the shell does not put a falsifiable claim on screen. */}
        <div
          style={{
            marginTop: isMobile ? 36 : 56,
            display: 'grid',
            gridTemplateColumns: isStacked ? '1fr' : 'repeat(2, minmax(0, 1fr))',
            gap: isStacked ? 32 : 48,
            alignItems: 'start',
          }}
        >
          <StoryColumn title={t('workflow.byHand.title')} steps={byHand} />
          <StoryColumn title={t('workflow.withAgent.title')} steps={withAgent} mono />
        </div>
      </div>
    </section>
  );
}

/**
 * A leading snake_case identifier — the shape every tool name in this catalogue
 * has (`snow_ensure_active_update_set`, `snow_manage_flow`). Used to set the
 * tool name in mono in the right-hand column.
 *
 * This used to split on the first em dash instead, which meant the two steps
 * whose copy happens to use a comma rather than a dash rendered as flat prose
 * while the other five got the mono treatment — an invisible dependency of the
 * layout on a punctuation choice in two locale files. Matching the identifier
 * itself is what the column actually means, and it survives a copy edit.
 */
const TOOL_NAME = /^[a-z][a-z0-9]*(?:_[a-z0-9]+)+/;

/**
 * One side of the before/after. With `mono`, a step that opens on a tool name
 * gets that name in mono and the rest as prose; anything else renders as prose,
 * which is what the by-hand column wants anyway.
 */
function StoryColumn({ title, steps, mono }: { title: string; steps: string[]; mono?: boolean }) {
  return (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: 16 }}>
        {title}
      </div>
      <ol style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {steps.map((step, i) => {
          const head = mono ? step.match(TOOL_NAME)?.[0] ?? null : null;
          const tail = head ? step.slice(head.length) : step;
          return (
            <li
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '22px minmax(0, 1fr)',
                gap: 10,
                padding: '12px 0',
                borderTop: i === 0 ? 'none' : '1px solid var(--border)',
              }}
            >
              <span
                style={{
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                  color: 'var(--fg-dim)',
                  paddingTop: 3,
                }}
              >
                {String(i + 1).padStart(2, '0')}
              </span>
              <span style={{ fontSize: 14, lineHeight: 1.6, color: 'var(--fg-muted)', overflowWrap: 'anywhere' }}>
                {head && (
                  <span style={{ fontFamily: 'var(--font-mono)', fontSize: 13, color: 'var(--fg)' }}>
                    {head}
                  </span>
                )}
                {tail}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function StoryMock() {
  const { t } = useTranslation('marketing');
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div
        style={{
          background: 'var(--ink-050)',
          border: '1px solid var(--border-strong)',
          borderRadius: 12,
          padding: 18,
          boxShadow: 'var(--shadow-2)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            marginBottom: 12,
          }}
        >
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--fg-dim)',
              letterSpacing: '0.06em',
            }}
          >
            {t('workflow.story.label')}
          </span>
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 11,
              color: 'var(--fg)',
            }}
          >
            {t('workflow.story.id')}
          </span>
          <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
            <Tag tone="neutral">{t('workflow.story.tagJira')}</Tag>
            <Tag tone="warn">{t('workflow.story.tagP1')}</Tag>
          </span>
        </div>
        <div
          style={{
            fontSize: 16,
            color: 'var(--fg)',
            fontWeight: 500,
            marginBottom: 10,
            letterSpacing: '-0.005em',
          }}
        >
          {t('workflow.story.title')}
        </div>
        <div
          style={{
            fontSize: 13,
            color: 'var(--fg-muted)',
            lineHeight: 1.6,
            marginBottom: 12,
          }}
        >
          {t('workflow.story.body')}
        </div>
        <div
          style={{
            paddingTop: 10,
            borderTop: '1px solid var(--border)',
            display: 'flex',
            gap: 10,
            flexWrap: 'wrap',
            fontSize: 11,
            color: 'var(--fg-dim)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          <span>{t('workflow.story.metaAcceptance')}</span>
          <span>·</span>
          <span>{t('workflow.story.metaCI')}</span>
          <span>·</span>
          <span>{t('workflow.story.metaComponent')}</span>
        </div>
      </div>

      <div
        style={{
          background: 'var(--ink-050)',
          border: '1px solid var(--border)',
          borderRadius: 10,
          padding: 14,
          fontFamily: 'var(--font-mono)',
          fontSize: 12,
          lineHeight: 1.7,
          color: 'var(--fg)',
        }}
      >
        <div style={{ marginBottom: 4 }}>{t('workflow.story.shellPullCmd')}</div>
        <div style={{ color: 'var(--fg-muted)' }}>
          {t('workflow.story.shellMappedPrefix')}{' '}
          <span style={{ color: 'var(--fg)' }}>{t('workflow.story.shellMappedTable')}</span>{' '}
          {t('workflow.story.shellMappedSuffix')}
        </div>
        {/* The third line here read "▸ ready for plan_enter". That tool is not
            on either path this page sells: it is a Serac CLI/TUI tool
            (packages/opencode/src/tool/plan.ts), absent from the 444 in the
            servicenow-mcp manifest and absent from the portal backend — on main
            the only mention of the name in the package is prose in a generated
            doc. The note directly under this mock carves out jira_get_issue and
            then says everything else is identical, which turns the mismatch into
            an explicit claim rather than an omission, so the call comes out
            until the copy names one that exists on the install path. */}
      </div>
    </div>
  );
}

/* ---------------- FINAL CTA ---------------- */

function FinalCTA({ onOpenApp }: { onOpenApp: () => void }) {
  const { t } = useTranslation('marketing');
  const isMobile = useIsMobile();
  return (
    <section
      style={{
        padding: isMobile ? '56px 16px' : '80px 32px',
        borderTop: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          maxWidth: 800,
          margin: '0 auto',
          textAlign: 'center',
        }}
      >
        <h2
          className="t-display"
          style={{ fontSize: 'clamp(28px, 5vw, 44px)', margin: '0 0 20px', letterSpacing: '-0.02em', lineHeight: 1.1 }}
        >
          {t('finalCTA.headlineLine1')}
          <br />
          {t('finalCTA.headlineLine2')}
        </h2>
        <p
          style={{
            fontSize: isMobile ? 15 : 17,
            color: 'var(--fg-muted)',
            maxWidth: 540,
            margin: '0 auto 32px',
            lineHeight: 1.55,
          }}
        >
          {t('finalCTA.subtitle')}
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Button variant="ice" size={isMobile ? 'md' : 'lg'} onClick={onOpenApp} iconRight={<Icon.arrow s={14} />}>
            {t('finalCTA.openDashboard')}
          </Button>
          <Button
            variant="ghost"
            size={isMobile ? 'md' : 'lg'}
            icon={<Icon.terminal s={14} />}
            aria-label={t('hero.ctaCliCopy')}
            onClick={() => copyToClipboard(t('finalCTA.ctaCli'), t('hero.ctaCliCopied'))}
          >
            <span className="t-mono" style={{ fontSize: 13 }}>
              {t('finalCTA.ctaCli')}
            </span>
          </Button>
        </div>
        <div style={{ marginTop: 20 }}>
          <a href={REPO_URL} target="_blank" rel="noopener noreferrer" style={tapTargetWrap}>
            <Button variant="quiet" size="sm" icon={<Icon.github s={13} />}>
              {t('finalCTA.githubRepo')}
            </Button>
          </a>
        </div>
      </div>
    </section>
  );
}
